import React, { useEffect, useState } from 'react'
import SmartLink from '../SmartLink'
import { setCookie } from 'cookies-next'
import { REGION_COOKIE, pathWithRegion, regionDisplay, isRegion } from '../../utils/regions'
import styles from './styles.module.css'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year

/**
 * Where a dismissal is remembered. localStorage, deliberately NOT a cookie:
 * this is a purely client-side "don't tell me again", the server never needs it,
 * and every cookie is sent on every request. The two preference cookies stay the
 * only ones we set (INSTRUCTIONS §3).
 */
export const GEO_SUGGEST_DISMISSED_KEY = 'vrs.geoSuggestDismissed'

/**
 * Soft region-mismatch notice (humans only).
 *
 * Shown when the store ON SCREEN is not the store the visitor's geo-IP puts them
 * in: a real link to the right store, plus a dismiss. NEVER auto-redirects and
 * never affects crawlers — it runs client-side after hydration only.
 *
 * ── Why it now fires with a region cookie present (2026-09-01) ────────────────
 * It shipped (08ed80b2) gated on "no region cookie", i.e. a strictly first-visit
 * nudge. That skipped the case that actually costs sales: a visitor carrying a
 * region cookie that disagrees with where they are. A customer in Dubai looking
 * at the US store saw "Sold out" (US pedals really are) with nothing on the page
 * suggesting he was in the wrong store, and reported that we had no pedals.
 *
 * The gate also hid a second bug: dismissing called `persistChoice(currentRegion)`,
 * WRITING the region cookie for whatever store the visitor happened to land on.
 * "Stay here" — a "not now" — silently became a permanent pin to the wrong store,
 * and guaranteed the banner could never speak again. Dismissing now records only
 * a dismissal; a region PREFERENCE is set only by an explicit region choice (the
 * footer switcher, the country picker, or accepting this banner).
 *
 * ── Why once per GEO REGION, and not a standing notice ────────────────────────
 * The region cookie is only ever written by deliberate action, so a mismatch is
 * often a real choice (an expat shipping home, a gift abroad, a forwarding
 * address) — and this audience skews to VPNs and mobile carriers, exactly where
 * country-level geo-IP is least reliable. A permanent bar arguing with a choice
 * the user made is a conversion risk. Remembering the dismissal against the GEO
 * region gives one clear nudge per situation: a visitor who is told "you look to
 * be in International" and says no is left alone, but if their geo later changes
 * (they travelled, or they were mis-pinned differently) that is a new situation
 * and worth one more nudge.
 *
 * Only rendered on pages that actually show regional pricing (`isCommerce`) —
 * on /privacy there is no store to be in the wrong one of.
 */
function GeoSuggestBanner({ currentRegion, slug, isCommerce = true }) {
  const [suggestedRegion, setSuggestedRegion] = useState(null)

  useEffect(() => {
    if (!isCommerce || !isRegion(currentRegion)) {
      return undefined
    }

    let cancelled = false
    // Native fetch (task #155: dropped the client-side axios import). Same-origin
    // /api/* only; best-effort — any failure (network or non-2xx) just skips the
    // banner. fetch does not reject on HTTP error, so guard on `res.ok`.
    fetch('/api/getRegion')
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (cancelled || !body) {
          return
        }
        const geoRegion = body.region
        // `region: null` means the geo lookup does not know (see lib/geoIp.js).
        // Never guess — a wrong "you look to be in X" is worse than silence.
        if (!isRegion(geoRegion) || geoRegion === currentRegion) {
          return
        }
        if (dismissedRegions().includes(geoRegion)) {
          return
        }
        setSuggestedRegion(geoRegion)
      })
      .catch(() => {
        /* geo lookup is best-effort; no banner on failure */
      })

    return () => {
      cancelled = true
    }
  }, [currentRegion, isCommerce])

  if (!suggestedRegion) {
    return null
  }

  const here = regionDisplay(currentRegion)
  const there = regionDisplay(suggestedRegion)
  const targetPath = pathWithRegion(`/${slug || ''}`, suggestedRegion)

  // Accepting IS an explicit region choice, so it persists like one.
  const acceptSuggestion = () => {
    setCookie(REGION_COOKIE, suggestedRegion, { maxAge: COOKIE_MAX_AGE, path: '/' })
    setSuggestedRegion(null)
  }

  const dismiss = () => {
    rememberDismissal(suggestedRegion)
    setSuggestedRegion(null)
  }

  return (
    <div className={styles.banner} role="region" aria-label="Region suggestion">
      <span className={styles.message}>
        You&apos;re viewing the {here.label} store, but you look to be shopping from{' '}
        {there.label}. Prices and availability there are shown in {there.currency} (
        {there.symbol}).
      </span>
      <div className={styles.actions}>
        {/* Real anchor: accept sets the cookie then navigates. */}
        <SmartLink className={styles.acceptLink} href={targetPath} onClick={acceptSuggestion}>
          Go to the {there.label} store
        </SmartLink>
        <button
          className={styles.dismissButton}
          type="button"
          onClick={dismiss}
          aria-label="Dismiss region suggestion"
        >
          Stay here
        </button>
      </div>
    </div>
  )
}

/**
 * Geo regions this visitor has already been nudged about and declined.
 * Storage access can THROW outright (Safari private mode, blocked site data), so
 * every read/write is guarded — an unusable store just means the notice is not
 * remembered across page loads, never a broken banner.
 */
function dismissedRegions() {
  try {
    const raw = window.localStorage.getItem(GEO_SUGGEST_DISMISSED_KEY)
    return raw ? String(raw).split(',').filter(Boolean) : []
  } catch (e) {
    return []
  }
}

function rememberDismissal(region) {
  try {
    const next = Array.from(new Set([...dismissedRegions(), region]))
    window.localStorage.setItem(GEO_SUGGEST_DISMISSED_KEY, next.join(','))
  } catch (e) {
    /* nothing to remember it with; the notice stays a per-page-load nudge */
  }
}

export default GeoSuggestBanner
